// Email service using EmailJS or SendGrid
import emailjs from '@emailjs/browser';

// Initialize EmailJS
const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
const EMAILJS_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
const EMAILJS_PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

// Email templates
export const EMAIL_TEMPLATES = {
  WELCOME: 'welcome_email',
  APPROVAL: 'approval_email',
  APPLICATION_RECEIVED: 'application_received',
  APPLICATION_UPDATE: 'application_update',
  EVENT_REGISTRATION: 'event_registration',
  EVENT_REMINDER: 'event_reminder',
  MEMBERSHIP_CONFIRMATION: 'membership_confirmation',
  MEMBERSHIP_EXPIRY: 'membership_expiry',
  PASSWORD_RESET: 'password_reset',
  NEWSLETTER: 'newsletter',
  ANNOUNCEMENT: 'announcement'
};

// Send email using EmailJS
export const sendEmail = async (templateId, templateParams) => {
  try {
    const response = await emailjs.send(
      EMAILJS_SERVICE_ID,
      templateId,
      templateParams,
      EMAILJS_PUBLIC_KEY
    );
    
    console.log('Email sent successfully:', response);
    return response;
  } catch (error) {
    console.error('Error sending email:', error);
    throw error;
  }
};

// Send welcome email to new users
export const sendWelcomeEmail = async (userEmail, userName) => {
  const templateParams = {
    to_email: userEmail,
    to_name: userName,
    from_name: 'APN Team',
    subject: 'Welcome to the Accelerated Professionals Network!',
    message: `
      Welcome to APN, ${userName}!
      
      Your account has been created and is pending approval. 
      You'll receive another email once your account is approved.
      
      In the meantime, feel free to explore our public content.
      
      Best regards,
      The APN Team
    `
  };

  return sendEmail(EMAIL_TEMPLATES.WELCOME, templateParams);
};

// Send approval notification
export const sendApprovalEmail = async (userEmail, userName) => {
  const templateParams = {
    to_email: userEmail,
    to_name: userName,
    from_name: 'APN Team',
    subject: 'Your APN Account Has Been Approved!',
    message: `
      Congratulations ${userName}!
      
      Your APN account has been approved. You now have access to:
      - Member directory
      - Job board
      - Project board
      - Forums and groups
      - Exclusive events
      
      Login now to start networking: ${window.location.origin}/login
      
      Welcome to the community!
      The APN Team
    `
  };

  return sendEmail(EMAIL_TEMPLATES.APPROVAL, templateParams);
};

// Send job application confirmation
export const sendJobApplicationEmail = async (userEmail, userName, jobTitle, companyName) => {
  const templateParams = {
    to_email: userEmail,
    to_name: userName,
    from_name: 'APN Team',
    subject: `Application Received: ${jobTitle}`,
    message: `
      Hi ${userName},
      
      We've received your application for the position:
      ${jobTitle} at ${companyName}
      
      Your application has been forwarded to the employer.
      You'll be notified of any updates.
      
      Good luck!
      The APN Team
    `
  };

  return sendEmail(EMAIL_TEMPLATES.APPLICATION_RECEIVED, templateParams);
};

// Send event registration confirmation
export const sendEventRegistrationEmail = async (userEmail, userName, eventTitle, eventDate) => {
  const templateParams = {
    to_email: userEmail,
    to_name: userName,
    from_name: 'APN Team',
    subject: `Event Registration Confirmed: ${eventTitle}`,
    message: `
      Hi ${userName},
      
      You're registered for: ${eventTitle}
      Date: ${eventDate}
      
      We'll send you event details and reminders closer to the date.
      
      Looking forward to seeing you there!
      The APN Team
    `
  };

  return sendEmail(EMAIL_TEMPLATES.EVENT_REGISTRATION, templateParams);
};

// Send membership confirmation
export const sendMembershipConfirmationEmail = async (userEmail, userName, tierName, expiryDate) => {
  const templateParams = {
    to_email: userEmail,
    to_name: userName,
    from_name: 'APN Team',
    subject: `Membership Confirmed: ${tierName}`,
    message: `
      Hi ${userName},
      
      Your ${tierName} membership is now active!
      
      Membership expires: ${expiryDate}
      
      You now have access to all premium features.
      
      Thank you for supporting APN!
      The APN Team
    `
  };

  return sendEmail(EMAIL_TEMPLATES.MEMBERSHIP_CONFIRMATION, templateParams);
};

// Send newsletter
export const sendNewsletter = async (subscribers, subject, content) => {
  const promises = subscribers.map(subscriber => {
    const templateParams = {
      to_email: subscriber.email,
      to_name: subscriber.name,
      from_name: 'APN Team',
      subject: subject,
      message: content
    };

    return sendEmail(EMAIL_TEMPLATES.NEWSLETTER, templateParams);
  });

  try {
    await Promise.all(promises);
    console.log('Newsletter sent to all subscribers');
  } catch (error) {
    console.error('Error sending newsletter:', error);
    throw error;
  }
};

// Notification system for in-app notifications
export const createNotification = async (userId, type, title, message, data = {}) => {
  try {
    const notification = {
      userId,
      type,
      title,
      message,
      data,
      read: false,
      createdAt: new Date(),
      updatedAt: new Date()
    };

    // Store in Firestore
    const { addDoc, collection } = await import('firebase/firestore');
    const { db } = await import('../firebase');
    
    const docRef = await addDoc(collection(db, 'notifications'), notification);
    return docRef.id;
  } catch (error) {
    console.error('Error creating notification:', error);
    throw error;
  }
};

// Get user notifications
export const getUserNotifications = async (userId, limit = 20) => {
  try {
    const { query, collection, where, orderBy, limit: firestoreLimit, getDocs } = await import('firebase/firestore');
    const { db } = await import('../firebase');

    const notificationsQuery = query(
      collection(db, 'notifications'),
      where('userId', '==', userId),
      orderBy('createdAt', 'desc'),
      firestoreLimit(limit)
    );

    const querySnapshot = await getDocs(notificationsQuery);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting notifications:', error);
    throw error;
  }
};

// Mark notification as read
export const markNotificationAsRead = async (notificationId) => {
  try {
    const { doc, updateDoc } = await import('firebase/firestore');
    const { db } = await import('../firebase');

    await updateDoc(doc(db, 'notifications', notificationId), {
      read: true,
      updatedAt: new Date()
    });
  } catch (error) {
    console.error('Error marking notification as read:', error);
    throw error;
  }
};

// Send announcement to all members
export const sendAnnouncement = async (title, message, actionUrl = null) => {
  try {
    // Get all approved members
    const { getAllUsers } = await import('./firestoreService');
    const users = await getAllUsers(1000); // Get up to 1000 users
    const approvedUsers = users.filter(user => user.status === 'approved' && user.email);

    if (approvedUsers.length === 0) {
      throw new Error('No approved members found to send announcement to');
    }

    // Create announcement template parameters
    const templateParams = {
      from_name: 'APN Team',
      subject: `APN Announcement: ${title}`,
      announcement_title: title,
      announcement_message: message,
      action_url: actionUrl || '',
      action_button: actionUrl ? `<a href="${actionUrl}" style="background-color: #3d67ad; color: white; padding: 12px 24px; text-decoration: none; border-radius: 5px;">Learn More</a>` : ''
    };

    // Send emails in batches to avoid rate limits
    const batchSize = 10; // Smaller batches for EmailJS
    const results = [];

    for (let i = 0; i < approvedUsers.length; i += batchSize) {
      const batch = approvedUsers.slice(i, i + batchSize);

      for (const user of batch) {
        try {
          const userParams = {
            ...templateParams,
            to_email: user.email,
            to_name: user.firstName || 'Member'
          };

          const result = await sendEmail(EMAIL_TEMPLATES.ANNOUNCEMENT, userParams);
          results.push({ success: true, email: user.email, result });

          // Add small delay between emails
          await new Promise(resolve => setTimeout(resolve, 200));
        } catch (error) {
          console.error(`Error sending announcement to ${user.email}:`, error);
          results.push({ success: false, email: user.email, error: error.message });
        }
      }

      // Add delay between batches
      if (i + batchSize < approvedUsers.length) {
        await new Promise(resolve => setTimeout(resolve, 2000));
      }
    }

    // Store announcement in database
    const { addDoc, collection, serverTimestamp } = await import('firebase/firestore');
    const { db } = await import('../firebase');

    await addDoc(collection(db, 'announcements'), {
      title,
      message,
      actionUrl,
      sentAt: serverTimestamp(),
      recipientCount: approvedUsers.length,
      successCount: results.filter(r => r.success).length,
      failureCount: results.filter(r => !r.success).length
    });

    return {
      success: true,
      totalRecipients: approvedUsers.length,
      successCount: results.filter(r => r.success).length,
      failureCount: results.filter(r => !r.success).length,
      results
    };
  } catch (error) {
    console.error('Error sending announcement:', error);
    throw error;
  }
};
